import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE, IMAGES } from "@/lib/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#F8FAF7]/95 backdrop-blur-md border-b border-[#1B4332]/10 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
        <a
          data-testid="brand-logo-link"
          href="#home"
          className="flex items-center gap-3 group"
        >
          <span
            className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full overflow-hidden flex items-center justify-center border-2 shadow-md transition-all bg-white ${
              scrolled ? "border-[#D4AF37]" : "border-[#D4AF37]/80"
            }`}
          >
            <img
              src={IMAGES.logo}
              alt="Navilukallu Homestay logo"
              className="w-full h-full object-contain p-0.5"
              loading="eager"
            />
          </span>
          <div className="leading-tight">
            <div
              className={`font-heading text-lg sm:text-xl font-semibold tracking-tight ${
                scrolled ? "text-[#1B4332]" : "text-white"
              }`}
            >
              Navilukallu
            </div>
            <div
              className={`text-[10px] sm:text-[11px] tracking-[0.28em] uppercase ${
                scrolled ? "text-[#D4AF37]" : "text-[#D4AF37]"
              }`}
            >
              Homestay
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${
                scrolled ? "text-[#1A1A1A]/80" : "text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <a
            href="#booking"
            data-testid="navbar-book-now-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#1A1A1A] text-sm font-medium hover:scale-[1.03] hover:shadow-[0_0_24px_rgba(212,175,55,0.45)] transition-all duration-300"
          >
            Book Now
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 rounded-full ${
            scrolled ? "text-[#1B4332]" : "text-white"
          }`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[560px]" : "max-h-0"
        } bg-[#1B4332]`}
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={closeMenu}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="py-2.5 text-white/85 hover:text-[#D4AF37] text-base border-b border-white/5 last:border-0"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#booking"
            onClick={closeMenu}
            data-testid="mobile-book-now-button"
            className="mt-4 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#D4AF37] text-[#1A1A1A] font-medium"
          >
            Book Your Stay
          </a>
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="mt-2 text-center text-white/70 text-sm py-2"
          >
            {SITE.phoneDisplay}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
